import { PanelLeftCloseIcon, PanelLeftOpenIcon } from "lucide-react";

import { IgIconButton } from "@intelligems/ig-theme/IgIconButton";
import { useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { VARIATION_BUTTON_MIN_WIDTH } from "@src/features/experienceBuilder/constants";
import { TestGroupSwitcher } from "@src/features/experienceBuilder/modifications/common/TestGroupSwitcher";
import { AiAssistantButton } from "@src/features/experienceBuilder/toolbar/AiAssistantButton";
import { DeviceSelector } from "@src/features/experienceBuilder/toolbar/DeviceSelector";
import { ModeSelector } from "@src/features/experienceBuilder/toolbar/ModeSelector";
import { MoreMenuButton } from "@src/features/experienceBuilder/toolbar/MoreMenuButton";
import { Navigator } from "@src/features/experienceBuilder/toolbar/Navigator";
import { ThemeSelector } from "@src/features/experienceBuilder/toolbar/ThemeSelector";
import { useSelector } from "@src/store";
import { selectedExperience } from "@src/store/slices/experiences";
import { selectedVariations } from "@src/store/slices/variations";

const PANEL_TOGGLE_BUTTON_WIDTH = 34;
const LEFT_STACK_GAP = 4;
const WRAPPER_GAP = 2;
const WRAPPER_PADDING_AND_BORDER = 8;

const getLeftStackMinWidth = (
  isExperiment: boolean,
  variationCount: number
) => {
  if (!isExperiment) {
    return PANEL_TOGGLE_BUTTON_WIDTH;
  }
  return (
    variationCount * VARIATION_BUTTON_MIN_WIDTH +
    Math.max(0, variationCount - 1) * WRAPPER_GAP +
    WRAPPER_PADDING_AND_BORDER +
    PANEL_TOGGLE_BUTTON_WIDTH +
    LEFT_STACK_GAP
  );
};

interface PreviewToolbarProps {
  isLeftPanelOpen: boolean;
  onToggleLeftPanel: () => void;
}

export const PreviewToolbar = ({
  isLeftPanelOpen,
  onToggleLeftPanel,
}: PreviewToolbarProps) => {
  const theme = useTheme();
  const isExperiment = useSelector(
    (state) => selectedExperience(state).category === "experiment"
  );
  const variationCount = useSelector(
    (state) => selectedVariations(state).length
  );
  const leftStackMinWidth = `${getLeftStackMinWidth(isExperiment, variationCount)}px`;

  return (
    <Box sx={{ backgroundColor: theme.igBackgroundColors.baseLightHigh }}>
      <Box
        sx={{
          width: "100%",
          borderBottom: `1px solid ${theme.igBorders.core.default}`,
          overflowX: "auto",
          overflowY: "hidden",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            padding: "0px 12px 0px 6px",
            height: "48px",
            alignItems: "center",
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ flex: 1, minWidth: leftStackMinWidth }}
          >
            <IgIconButton
              onClick={onToggleLeftPanel}
              aria-label={
                isLeftPanelOpen
                  ? "Collapse modifications panel"
                  : "Expand modifications panel"
              }
            >
              {isLeftPanelOpen ? (
                <PanelLeftCloseIcon size={18} />
              ) : (
                <PanelLeftOpenIcon size={18} />
              )}
            </IgIconButton>
            {isExperiment && <TestGroupSwitcher />}
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            spacing={2}
            flex={1}
          >
            <Navigator />
            <ThemeSelector />
            <DeviceSelector />
            <ModeSelector />
            <AiAssistantButton />
            <MoreMenuButton />
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
